import { cloneDeep } from 'lodash'
import moment from 'moment'
import { Facade, TableDataAndJournalParams } from '../../../common/Facade'
import { FilterOptions } from '../../../common/types/roots/FilterOptions'
import { FilterSettings } from '../../../common/types/roots/FilterSettings'
import { FilterSettingsForm } from '../../../common/types/roots/FilterSettingsForm'
import { Option } from '../../../common/types/Option'
import { BudgetType, MutuallyRedeemingLevel } from '../../../common/types/roots/fields'
import { reportOwnIncomeFilterOptions, ownIncomeKatDefaults } from '../report-own-income'
import { ReportOwnIncomeFilterSettings } from '../settings/ReportOwnIncomeFilterSettings'
import { ReportOwnIncomeFilterSettingsForm } from '../settings-form/ReportOwnIncomeFilterSettingsForm'
import { ReportOwnIncomeFilterOptions } from '../options/ReportOwnIncomeFilterOptions'
import { ReportOwnIncomeJournalRequest } from '../journal/ReportOwnIncomeJournal'
import { OwnIncomeResponse } from '../table-data/OwnIncomeTableData'

export class ReportOwnIncomeFacade implements Facade {

    getFilterOptions(): FilterOptions {
        return cloneDeep(reportOwnIncomeFilterOptions) as unknown as FilterOptions
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        const s = settings as unknown as ReportOwnIncomeFilterSettings
        const o = options as unknown as ReportOwnIncomeFilterOptions

        if (s.reportName !== 'OWN-INCOME') {
            throw new Error('error_type_filter_settings')
        }

        const reportName = o.reportNames.find(it => it.value === s.reportName)
        if (!reportName) throw new Error('error_not_found_report_name')

        const reportType = o.reportTypes.find(it => it.value === s.reportType)
        if (!reportType) throw new Error('error_not_found_report_type')

        const classificationType = o.classificationTypes.find(it => it.value === s.classificationType)
        if (!classificationType) throw new Error('error_not_found_class_type')

        const dataSource = o.dataSources.find(it => it.value === s.dataSource)
        if (!dataSource) throw new Error('error_not_found_data_source')

        const mutuallyRedeemingOption = o.mutuallyRedeeming.find(it => it.value === s.vzk)
        if (!mutuallyRedeemingOption) throw new Error('error_not_found_mutually_redeeming_option')

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        ;(s.mutuallyRedeemingLevels ?? []).forEach(level => {
            const option = o.mutuallyRedeemingLevels.find(it => it.value === level)
            if (option) mutuallyRedeemingLevelOptions.push({ ...option })
        })

        const measureUnitOption = o.measureUnits.find(it => it.value === s.measureUnit)
        if (!measureUnitOption) throw new Error('error_not_found_measure_unit')

        const regions: Option<string>[] = []
        ;(s.regions ?? []).forEach(region => {
            const option = o.regions.find(it => it.value === region)
            if (option) regions.push({ ...option })
        })

        const statuses = cloneDeep(
            o.statuses.filter(it => s.statuses.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = []
        ;(s.budgetTypes ?? []).forEach(budget => {
            const opt = o.budgetTypes.find(item => item.value === budget)
            if (opt) budgetTypes.push({ ...opt })
        })

        const katFilter: Option<number>[] = []
        ;(s.kat_filter ?? []).forEach(kat => {
            const opt = o.katOptions.find(item => item.value === kat)
            if (opt) katFilter.push({ ...opt })
        })

        const form: ReportOwnIncomeFilterSettingsForm = {
            key: 'OWN-INCOME',
            reportName: { ...reportName },
            reportType: { ...reportType },
            classificationType: { ...classificationType },
            dataSource: { ...dataSource },
            mutuallyRedeeming: { ...mutuallyRedeemingOption },
            measureUnit: { ...measureUnitOption },
            roundUp: s.roundUpTo != null,
            roundUpTo: s.roundUpTo ?? null,
            regions,
            date: s.date ? new Date(s.date) : null,
            statuses,
            budgetTypes,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            katFilter,
        }

        return form as unknown as FilterSettingsForm
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        const f = form as unknown as ReportOwnIncomeFilterSettingsForm

        if (f.key !== 'OWN-INCOME') throw new Error('error_type_filter_settingsForm')
        if (f.mutuallyRedeeming == null) throw new Error('error_input_mutually_redeeming')
        if (f.measureUnit == null) throw new Error('error_input_measure_unit')
        if (!f.regions || f.regions.length === 0) throw new Error('error_select_region')
        if (!f.date) throw new Error('error_input_date')
        if (!f.budgetTypes || f.budgetTypes.length === 0) throw new Error('error_select_budget')

        let roundUpTo: number | null = null
        if (f.roundUp) {
            roundUpTo = f.roundUpTo ?? null
        }

        const strDate = moment(f.date).format('YYYY-MM-DD')
        const rawStatuses = f.statuses.map(s => s.value)
        const resolvedStatuses = rawStatuses.includes('0') ? ['26', '39'] : rawStatuses

        const settings: ReportOwnIncomeFilterSettings = {
            reportName: 'OWN-INCOME',
            reportType: f.reportType.value,
            classificationType: f.classificationType.value,
            dataSource: f.dataSource.value,
            vzk: f.mutuallyRedeeming.value,
            measureUnit: f.measureUnit.value,
            roundUpTo,
            date: strDate,
            regions: f.regions.map(r => r.value),
            statuses: resolvedStatuses as any,
            budgetTypes: f.budgetTypes.map(b => b.value),
            mutuallyRedeemingLevels: f.mutuallyRedeemingLevels?.map(l => l.value) ?? [],
            kat_filter: f.katFilter && f.katFilter.length > 0 ? f.katFilter.map(k => k.value) : null,
        }

        return settings as unknown as FilterSettings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportOwnIncomeFilterOptions)

        const emptyForm: ReportOwnIncomeFilterSettingsForm = {
            key: 'OWN-INCOME',
            reportName: options.reportNames.find(it => it.value === 'OWN-INCOME')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            regions: [],
            date: moment().subtract(1, 'day').toDate(),
            statuses: [options.statuses.find(it => it.value === '39')!],
            budgetTypes: [],
            mutuallyRedeemingLevels: [],
            katFilter: cloneDeep(options.katOptions.filter(o => ownIncomeKatDefaults.includes(o.value))),
        }

        return emptyForm as unknown as FilterSettingsForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        const f = form as unknown as ReportOwnIncomeFilterSettingsForm

        if (f.key !== 'OWN-INCOME') throw new Error('error_type_filter_settingsForm')
        if (!f.date) throw new Error('error_input_date')
        if (!f.regions || f.regions.length === 0) throw new Error('error_input_region')
        if (f.measureUnit == null) throw new Error('error_input_measure_unit')
        if (!f.budgetTypes || f.budgetTypes.length === 0) throw new Error('error_select_budget')

        let roundUpTo: number | null = null
        if (f.roundUp) {
            roundUpTo = f.roundUpTo ?? null
        }

        const strDate = moment(f.date).format('YYYY-MM-DD')
        const rawStatuses = f.statuses.map(s => s.value)
        const resolvedStatuses = rawStatuses.includes('0') ? ['26', '39'] : rawStatuses

        const mutuallyRedeemingLevels = (f.mutuallyRedeemingLevels ?? []).map(l => {
            const match = l.value.match(/TYPE-(\d+)/)
            return match ? parseInt(match[1]) : 0
        })

        const params = {
            date: strDate,
            regions: f.regions.map(r => r.value),
            budget_types: f.budgetTypes.map(b => b.value),
            statuses: resolvedStatuses,
            vzk: f.mutuallyRedeeming?.value ?? 'WITHOUT',
            mutually_redeeming_levels: mutuallyRedeemingLevels,
            measureUnit: f.measureUnit.value,
            roundTo: roundUpTo,
            dataSource: f.dataSource.value,
            kat_filter: f.katFilter && f.katFilter.length > 0 ? f.katFilter.map(k => k.value) : null,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/own-income', {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params),
        })

        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }

        const tableData: OwnIncomeResponse = await response.json()

        const settings = this.formToSettings(cloneDeep(form)) as unknown as ReportOwnIncomeFilterSettings

        const journalParams: ReportOwnIncomeJournalRequest = {
            reportName: 'OWN-INCOME',
            name: templateName,
            excel_name: excelName,
            filter_settings: settings as unknown as any,
            user_id: userId,
            report_date: strDate,
            regions: f.regions.map(r => r.value),
            measure_unit: f.measureUnit.value,
            block1: tableData.block1 ?? null,
            block2: tableData.block2 ?? null,
            block3: tableData.block3 ?? null,
        }

        return {
            tableData: tableData as unknown as any,
            journalParams: journalParams as unknown as any,
        }
    }
}
